import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    TextField,
    IconButton,
    InputAdornment,
    Divider,
    Chip,
    Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { logsApi } from '../utils/api';

interface EditLogsProps {
    isOpen: boolean;
    onClose: () => void;
    refreshTrigger?: number;
}

interface EditLog {
    id: number;
    layer_type: string;
    feature_id: string;
    action: string;
    changeset: string;
    edited_by: string;
    edited_at: string;
}

const EditLogs: React.FC<EditLogsProps> = ({ isOpen, onClose, refreshTrigger = 0 }) => {
    const [logs, setLogs] = useState<EditLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // Intersection observer for infinite scroll
    const observer = useRef<IntersectionObserver | null>(null);
    const lastLogElementRef = useCallback((node: HTMLElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setOffset(prev => prev + 20);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchLogs = useCallback(async (isNewSearch = false) => {
        setLoading(true);
        try {
            const currentOffset = isNewSearch ? 0 : offset;
            const response = await logsApi.getEditLogs({
                limit: 20,
                offset: currentOffset,
                search: searchTerm
            });

            if (response.success && response.data?.logs) {
                const newLogs = response.data.logs;
                setLogs(prev => isNewSearch ? newLogs : [...prev, ...newLogs]);
                setHasMore(newLogs.length === 20);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, offset]);

    // Debounced search effect or just effect on search/offset
    useEffect(() => {
        // If searchTerm changed, reset offset and logs
        const timeoutId = setTimeout(() => {
            if (offset === 0) {
                fetchLogs(true);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Load more when offset changes (triggered by observer)
    useEffect(() => {
        if (offset > 0) {
            fetchLogs(false);
        }
    }, [offset, fetchLogs]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setOffset(0); // Reset offset to trigger new search
        setHasMore(true);
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    const getActionColor = (action: string) => {
        switch (action) {
            case 'CREATE': return 'success';
            case 'UPDATE': return 'info';
            case 'DELETE': return 'error';
            default: return 'default';
        }
    };

    if (!isOpen) return null;

    return (
        <Paper sx={{
            position: 'absolute',
            top: 80,
            right: 380, // Positioned to the left of LayerActionPanel if open
            width: 350,
            maxHeight: 'calc(100vh - 100px)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000,
            p: 1,
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            boxShadow: 3
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Edit Logs</Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Divider />

            <Box sx={{ p: 1.5 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <List sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 600 }}>
                {logs.map((log, index) => {
                    let changes = {};
                    try {
                        changes = JSON.parse(log.changeset);
                    } catch (e) { }

                    return (
                        <ListItem
                            key={log.id}
                            ref={index === logs.length - 1 ? lastLogElementRef : null}
                            divider
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="subtitle2" component="span" fontWeight="bold">
                                            {log.edited_by}
                                        </Typography>
                                        <Chip
                                            label={log.action}
                                            size="small"
                                            color={getActionColor(log.action) as any}
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                    </Box>
                                }
                                secondary={
                                    <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {formatDate(log.edited_at)}
                                        </Typography>
                                        <Typography variant="caption" color="text.primary" display="block" sx={{ mt: 0.5 }}>
                                            {log.layer_type} • {log.feature_id.substring(0, 8)}...
                                        </Typography>
                                        <Tooltip title={JSON.stringify(changes, null, 2)}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: 'block',
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'help'
                                                }}
                                            >
                                                {JSON.stringify(changes)}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        </ListItem>
                    );
                })}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}
                {!loading && logs.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No logs found.
                        </Typography>
                    </Box>
                )}
            </List>
        </Paper>
    );
};

export default EditLogs;
